import {
  BillingCycle as PlanBillingCycle,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useReducedMotion,
} from 'motion/react';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PLAN_PRICING, TRIAL_DAYS } from '@/core/billing/plan-catalog';
import { useFormatEur } from '@/core/billing/use-format-eur';
import { BillingCycleToggle } from '@/core/components/billing/billing-cycle-toggle';
import { EnterpriseContactDialog } from '@/core/components/billing/enterprise-contact-dialog';
import {
  onboardingSoftSpring,
  onboardingSpring,
} from '@/core/components/onboarding/onboarding-motion';
import { useDictionary } from '@/core/providers/dictionary-provider';

type StartPath = 'free' | 'trial';

const TRIAL_PLANS = [WorkspacePlan.PLUS, WorkspacePlan.TEAMS] as const;

/** Light Studio selected option — brand wash (not Ink `border-foreground`). */
const SELECTED_OPTION_CLASS =
  'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] shadow-[var(--shadow-card)] ring-1 ring-[color:var(--brand-500)]/25';

const IDLE_OPTION_CLASS =
  'border-border/80 bg-card/60 hover:border-[color:var(--ghost-accent-border)] hover:bg-[color:var(--ghost-accent-bg)]/40';

const DETAIL_SURFACE_CLASS =
  'rounded-[var(--r-card)] border border-border bg-card px-4 py-3.5 shadow-[var(--shadow-card)]';

export interface PlanOptionListProps {
  selectedPlan: WorkspacePlan | null;
  /** Pass `null` when entering the trial path before Plus/Teams is chosen. */
  onPlanChange: (plan: WorkspacePlan | null) => void;
  billingCycle: PlanBillingCycle;
  onBillingCycleChange: (cycle: PlanBillingCycle) => void;
  currentPlan?: WorkspacePlan | null;
}

function pathFromPlan(plan: WorkspacePlan | null): StartPath | null {
  if (plan === WorkspacePlan.FREE) return 'free';
  if (plan === WorkspacePlan.PLUS || plan === WorkspacePlan.TEAMS)
    return 'trial';
  return null;
}

function priceFor(
  planKey: typeof WorkspacePlan.PLUS | typeof WorkspacePlan.TEAMS,
  billingCycle: PlanBillingCycle,
  planDict: {
    perSeatPerMonth: string;
    perSeatPerYear: string;
    listPriceAfterTrial: string;
  },
  formatEur: (amount: number) => string,
): string {
  const pricing =
    planKey === WorkspacePlan.TEAMS
      ? PLAN_PRICING[WorkspacePlan.TEAMS]
      : PLAN_PRICING[WorkspacePlan.PLUS];
  const amount =
    billingCycle === PlanBillingCycle.YEARLY ? pricing.yearly : pricing.monthly;
  const seat =
    billingCycle === PlanBillingCycle.YEARLY
      ? planDict.perSeatPerYear
      : planDict.perSeatPerMonth;
  return planDict.listPriceAfterTrial
    .replace('{price}', formatEur(amount))
    .replace('{seat}', seat);
}

/**
 * Dual-path plan chooser: Start free vs Start with trial, then Plus/Teams.
 * Equal visual weight — no recommended tint, no preselected paid plan.
 */
export function PlanOptionList({
  selectedPlan,
  onPlanChange,
  billingCycle,
  onBillingCycleChange,
}: PlanOptionListProps) {
  const dict = useDictionary();
  const planDict = dict.workspace.create.planSelection;
  const copy = dict.onboarding.plan;
  const reduceMotion = useReducedMotion();
  const formatEur = useFormatEur();
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [path, setPath] = useState<StartPath | null>(() =>
    pathFromPlan(selectedPlan),
  );
  const days = String(TRIAL_DAYS);

  const plans = useMemo(
    () => ({
      free: planDict.plans.free,
      plus: planDict.plans.plus,
      teams: planDict.plans.teams,
    }),
    [planDict.plans],
  );

  const choosePath = (next: StartPath) => {
    setPath(next);
    if (next === 'free') {
      onPlanChange(WorkspacePlan.FREE);
      return;
    }
    onPlanChange(null);
  };

  const selectTrialPlan = (plan: WorkspacePlan.PLUS | WorkspacePlan.TEAMS) => {
    setPath('trial');
    onPlanChange(plan);
  };

  return (
    <LayoutGroup id="plan-path-chooser">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(
            [
              {
                id: 'trial' as const,
                title: copy.pathTrialTitle.replace('{days}', days),
                description: copy.pathTrialDescription.replace('{days}', days),
                chip: copy.pathTrialChip.replace('{days}', days),
              },
              {
                id: 'free' as const,
                title: copy.pathFreeTitle,
                description: copy.pathFreeDescription,
                chip: null as string | null,
              },
            ] as const
          ).map((item) => {
            const isActive = path === item.id;
            return (
              <motion.button
                key={item.id}
                type="button"
                layout={!reduceMotion}
                transition={onboardingSpring}
                onClick={() => choosePath(item.id)}
                aria-pressed={isActive}
                className={cn(
                  'flex min-h-[5.5rem] flex-col items-start gap-1.5 rounded-[var(--r-card)] border px-4 py-3.5 text-start transition-[color,background-color,box-shadow,border-color] duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isActive ? SELECTED_OPTION_CLASS : IDLE_OPTION_CLASS,
                )}
              >
                {item.chip ? (
                  <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-[color:var(--ghost-accent-fg)]">
                    {item.chip}
                  </span>
                ) : null}
                <span className="text-[13.5px] font-semibold tracking-tight text-foreground">
                  {item.title}
                </span>
                <span className="text-pretty text-[13.5px] leading-snug text-muted-foreground">
                  {item.description}
                </span>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {path === 'free' ? (
            <motion.div
              key="free-detail"
              layoutId={reduceMotion ? undefined : 'plan-path-detail'}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
              transition={onboardingSoftSpring}
              className={DETAIL_SURFACE_CLASS}
            >
              <p className="text-[13.5px] font-semibold text-foreground">
                {plans.free.name}
                <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                  {formatEur(0)} · {planDict.freeForever}
                </span>
              </p>
              <ul className="mt-3 space-y-1.5">
                {plans.free.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-[13.5px] leading-snug text-muted-foreground"
                  >
                    <Check
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[color:var(--ghost-accent-fg)]"
                      aria-hidden
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ) : null}

          {path === 'trial' ? (
            <motion.div
              key="trial-detail"
              layoutId={reduceMotion ? undefined : 'plan-path-detail'}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
              transition={onboardingSoftSpring}
              className="space-y-3"
            >
              <div className="flex flex-col items-center sm:items-start">
                <BillingCycleToggle
                  billingCycle={billingCycle}
                  onBillingCycleChange={onBillingCycleChange}
                />
              </div>

              <p className="text-[13.5px] font-semibold text-foreground">
                {copy.pickTrialPlan}
              </p>

              <div
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label={copy.pickTrialPlan}
              >
                {TRIAL_PLANS.map((planKey) => {
                  const data =
                    planKey === WorkspacePlan.PLUS ? plans.plus : plans.teams;
                  const isSelected = selectedPlan === planKey;
                  return (
                    <button
                      key={planKey}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => selectTrialPlan(planKey)}
                      className={cn(
                        'flex min-h-14 flex-col items-start gap-1 rounded-[var(--r-card)] border px-3.5 py-3 text-start transition-[color,background-color,box-shadow,border-color] duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        isSelected ? SELECTED_OPTION_CLASS : IDLE_OPTION_CLASS,
                      )}
                    >
                      <span className="flex w-full items-baseline justify-between gap-2">
                        <span className="text-[13.5px] font-semibold text-foreground">
                          {data.name}
                        </span>
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {priceFor(planKey, billingCycle, planDict, formatEur)}
                        </span>
                      </span>
                      <span className="text-pretty text-[13.5px] leading-snug text-muted-foreground">
                        {data.description}
                      </span>
                    </button>
                  );
                })}
              </div>

              <AnimatePresence initial={false}>
                {selectedPlan === WorkspacePlan.PLUS ||
                selectedPlan === WorkspacePlan.TEAMS ? (
                  <motion.div
                    key="trial-features"
                    initial={reduceMotion ? false : { opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={
                      reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }
                    }
                    transition={
                      reduceMotion ? { duration: 0.12 } : onboardingSoftSpring
                    }
                    className={cn('overflow-hidden', DETAIL_SURFACE_CLASS)}
                  >
                    <ul className="space-y-1.5">
                      {(selectedPlan === WorkspacePlan.PLUS
                        ? plans.plus
                        : plans.teams
                      ).features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2 text-[13.5px] leading-snug text-muted-foreground"
                        >
                          <Check
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[color:var(--ghost-accent-fg)]"
                            aria-hidden
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-[13.5px] leading-snug text-muted-foreground">
                      {copy.trialReminder.replace('{days}', days)}
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {selectedPlan === WorkspacePlan.ENTERPRISE ? (
          <p className="text-center text-sm text-muted-foreground md:text-start">
            {copy.enterpriseSelected}
          </p>
        ) : null}

        <div className="flex justify-center md:justify-start">
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            onClick={() => {
              setPath(null);
              onPlanChange(WorkspacePlan.ENTERPRISE);
              setEnterpriseOpen(true);
            }}
          >
            {copy.enterpriseLink}
          </button>
        </div>

        <EnterpriseContactDialog
          open={enterpriseOpen}
          onOpenChange={setEnterpriseOpen}
        />
      </div>
    </LayoutGroup>
  );
}
